<template>
  <div class="container">
    <div class="header">
      <div class="header-text">
        <h1>جزئیات اتاق</h1>
        <span v-if="roomName" class="room-subtitle">{{ roomName }}</span>
      </div>
      <router-link to="/dashboard/rooms" class="back-btn">بازگشت</router-link>
    </div>

    <div class="gallery-section">
      <!-- Navigation Tabs -->
      <div class="tabs">
        <button :class="{active: tab==='paint'}" @click="tab='paint'">نقاشی 🎨</button>
        <button :class="{active: tab==='puzzle'}" @click="tab='puzzle'">پازل 🧩</button>
        <button :class="{active: tab==='shapes'}" @click="tab='shapes'">اشکال 🔺</button>
        <button :class="{active: tab==='letters'}" @click="tab='letters'">حروف 🔤</button>
        <button :class="{active: tab==='story'}" @click="tab='story'">داستان 📖</button>
        <button :class="{active: tab==='music'}" @click="tab='music'">موسیقی 🎵</button>
        <button :class="{active: tab==='connection'}" @click="tab='connection'">ارتباط 🔗</button>
        <button :class="{active: tab==='security'}" @click="tab='security'">تنظیمات ⚙️</button>
      </div>
      
      <!-- Section Headers -->
      <h2 v-if="tab==='paint'">گالری نقاشی‌ها 🎨</h2>
      <h2 v-else-if="tab==='puzzle'">جلسات پازل 🧩</h2>
      <h2 v-else-if="tab==='shapes'">جلسات اشکال 🔺</h2>
      <h2 v-else-if="tab==='letters'">جلسات حروف 🔤</h2>
      <h2 v-else-if="tab==='story'">داستان‌ها 📖</h2>
      <h2 v-else-if="tab==='music'">آهنگ‌های ساخته شده 🎵</h2>
      <h2 v-else-if="tab==='connection'">ماموریت‌های دنیای واقعی 🔗</h2>
      <h2 v-else-if="tab==='security'">تنظیمات امنیتی</h2>

      <!-- Global Loading State -->
      <div v-if="isLoadingAny" class="loading">
        <div class="spinner"></div>
        <p>در حال بارگذاری اطلاعات...</p>
      </div>

      <!-- 1. PAINT TAB -->
      <div v-if="tab==='paint' && !isLoadingAny">
         <div v-if="gallery.length === 0" class="empty">
            <div class="empty-icon">🎨</div>
            <p>هنوز نقاشی‌ای در این اتاق کشیده نشده است.</p>
         </div>
         <div v-else class="slider-container">
            <button class="nav-btn prev" @click="prevSlide" :disabled="currentIndex === 0">❮</button>
            <div class="slider-wrapper">
               <div class="slide" v-if="currentArt">
                  <div class="image-container">
                     <img :src="currentArt.imageBase64" alt="Painting" class="art-image" />
                  </div>
                  <div class="art-info">
                     <div class="info-header">
                       <h3>{{ currentArt.topic }}</h3>
                       <span class="date">{{ formatDate(currentArt.createdAt) }}</span>
                     </div>
                     
                     <div class="analysis">
                        <h4>تحلیل هوش مصنوعی</h4>
                        <div v-if="currentArt.analysisStatus === 'pending'" class="pending">
                          <span class="spinner-small" />
                          <span>تحلیل در حال انجام است...</span>
                        </div>
                        <div v-else>
                           <div class="callout positive">
                             <div class="callout-title">پیام تشویقی</div>
                             <div class="callout-text">{{ currentArt.positiveFeedback || '—' }}</div>
                           </div>

                           <div class="score-row">
                             <div class="score">
                               <div class="score-header">
                                 <span class="score-label">امتیاز خلاقیت</span>
                                 <span class="score-value">{{ safeScore(currentArt.creativityScore) }}/10</span>
                               </div>
                               <div class="score-bar">
                                 <div class="score-fill" :style="{ width: (safeScore(currentArt.creativityScore) * 10) + '%' }"></div>
                               </div>
                             </div>
                           </div>

                           <div class="details-grid">
                              <div class="detail"><div class="detail-label">مرحله رشد</div><div class="detail-text">{{ currentArt.developmentalStage || '—' }}</div></div>
                              <div class="detail"><div class="detail-label">حالت احساسی</div><div class="detail-text">{{ currentArt.emotionalState || '—' }}</div></div>
                              <div class="detail" v-if="currentArt.fineMotorSkills"><div class="detail-label">مهارت حرکتی</div><div class="detail-text">{{ currentArt.fineMotorSkills }}</div></div>
                              <div class="detail" v-if="currentArt.creativityLevel"><div class="detail-label">سطح خلاقیت</div><div class="detail-text">{{ currentArt.creativityLevel }}</div></div>
                           </div>

                           <div class="callout parent-advice">
                             <div class="callout-title">راهنمای والدین</div>
                             <div class="callout-text">{{ (currentArt.parentAdvice || currentArt.analysisSummary) || '—' }}</div>
                           </div>
                           
                           <div class="tags">
                             <span v-for="tag in currentArt.tags" :key="tag" class="tag">#{{ tag }}</span>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
            <button class="nav-btn next" @click="nextSlide" :disabled="currentIndex === gallery.length - 1">❯</button>
         </div>
         <div class="thumbnails" v-if="gallery.length > 0">
            <div v-for="(item, index) in gallery" :key="item.id" class="thumb" :class="{ active: index === currentIndex }" @click="currentIndex = index">
              <img :src="item.imageBase64" />
            </div>
         </div>
      </div>

      <!-- 2. PUZZLE TAB -->
      <div v-else-if="tab==='puzzle' && !isLoadingAny" class="tab-content">
          <div v-if="puzzles.length === 0" class="empty"><div class="empty-icon">🧩</div><p>نشستی ثبت نشده است.</p></div>
          <div v-else class="grid-layout">
            <div v-for="s in puzzles" :key="s.id" class="card-item">
              <div class="image-pair">
                <div class="img-col"><span class="img-label">الگو</span><img v-if="s.imageBase64" :src="s.imageBase64" loading="lazy"/></div>
                <div class="img-col"><span class="img-label">کار کودک</span><img v-if="s.solutionBase64" :src="s.solutionBase64" loading="lazy"/></div>
              </div>
              <div class="card-meta">
                <div class="meta-row"><span>زمان:</span> <strong>{{ fmtTime(s.durationSeconds) }}</strong></div>
                <div class="meta-row"><span>حرکات:</span> <strong>{{ s.moves }}</strong></div>
                <div class="meta-row"><span>تاریخ:</span> <strong>{{ formatDate(s.createdAt) }}</strong></div>
                <button class="btn-details" @click="openModal(s, 'puzzle')">مشاهده تحلیل</button>
              </div>
            </div>
          </div>
      </div>
      
      <!-- 3. SHAPES TAB -->
      <div v-else-if="tab==='shapes' && !isLoadingAny" class="tab-content">
          <div v-if="shapes.length === 0" class="empty"><div class="empty-icon">🔺</div><p>نشستی ثبت نشده است.</p></div>
          <div v-else class="grid-layout">
            <div v-for="s in shapes" :key="s.id" class="card-item">
              <div class="image-pair">
                <div class="img-col"><span class="img-label">الگو</span><img v-if="s.referenceBase64" :src="s.referenceBase64" loading="lazy"/></div>
                <div class="img-col"><span class="img-label">کار کودک</span><img v-if="s.compositionBase64" :src="s.compositionBase64" loading="lazy"/></div>
              </div>
              <div class="card-meta">
                <div class="meta-row"><span>زمان:</span> <strong>{{ fmtTime(s.durationSeconds) }}</strong></div>
                <div class="meta-row"><span>تعداد شکل:</span> <strong>{{ s.shapeCount }}</strong></div>
                <div class="meta-row"><span>تاریخ:</span> <strong>{{ formatDate(s.createdAt) }}</strong></div>
                <button class="btn-details" @click="openModal(s, 'shapes')">مشاهده تحلیل</button>
              </div>
            </div>
          </div>
      </div>

      <!-- 4. LETTERS TAB -->
      <div v-else-if="tab==='letters' && !isLoadingAny" class="tab-content">
          <div v-if="letters.length === 0" class="empty"><div class="empty-icon">🔤</div><p>نشستی ثبت نشده است.</p></div>
          <div v-else class="grid-layout">
            <div v-for="s in letters" :key="s.id" class="card-item">
              <div class="image-pair">
                <div class="img-col"><span class="img-label">سرمشق</span><img v-if="s.referenceBase64" :src="s.referenceBase64" loading="lazy"/></div>
                <div class="img-col"><span class="img-label">دست‌خط</span><img v-if="s.traceBase64" :src="s.traceBase64" loading="lazy"/></div>
              </div>
              <div class="card-meta">
                <div class="meta-row"><span>حرف:</span> <strong class="letter-badge">{{ s.letter }}</strong></div>
                <div class="meta-row"><span>سطح:</span> <strong>{{ s.level }}</strong></div>
                <div class="meta-row"><span>تاریخ:</span> <strong>{{ formatDate(s.createdAt) }}</strong></div>
                <button class="btn-details" @click="openModal(s, 'letters')">مشاهده تحلیل</button>
              </div>
            </div>
          </div>
      </div>

      <!-- 5. STORY TAB -->
      <div v-else-if="tab==='story' && !isLoadingAny" class="tab-content">
        <div class="controls-bar">
          <button class="btn-refresh" @click="storyRefetch">🔄 بروزرسانی لیست</button>
        </div>
        <div v-if="storySessions.length===0" class="empty"><div class="empty-icon">📖</div><p>هنوز گفتگویی ثبت نشده است.</p></div>
        <div v-else class="story-grid">
          <div v-for="sess in storySessions" :key="sess.id" class="story-card">
            <div class="story-head">
              <div class="story-title">{{ sess.title || 'داستان جدید' }}</div>
              <div class="story-date">{{ formatDate(sess.createdAt) }}</div>
            </div>
            
            <div class="chat-preview">
              <div v-for="(m, idx) in sess.messages.slice(-3)" :key="idx" class="mini-msg" :class="m.role">
                <span class="role-icon">{{ m.role === 'kid' ? '🧒' : '🤖' }}</span>
                <span class="msg-text">{{ truncate(m.text, 40) }}</span>
              </div>
            </div>

            <div class="analysis-preview" v-if="sess.analysisStatus === 'completed'">
               <div class="rating-badge">
                 <span>⭐ امتیاز کودک:</span>
                 <strong>{{ safeScore(sess.kidRating) }}/10</strong>
               </div>
            </div>
            <div v-else class="pending-badge">در انتظار تحلیل...</div>

            <button class="btn-details" @click="openModal(sess, 'story')">
              {{ sess.analysisStatus === 'completed' ? 'مشاهده تحلیل کامل' : 'درخواست تحلیل' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 6. MUSIC TAB -->
      <div v-else-if="tab==='music' && !isLoadingAny" class="tab-content">
        <div v-if="musicSessions.length === 0" class="empty"><div class="empty-icon">🎵</div><p>هنوز آهنگی ساخته نشده است.</p></div>
        <div v-else class="grid-layout">
           <div v-for="s in musicSessions" :key="s.id" class="card-item music-card">
            <div class="music-icon-large">🎻</div>
            <div class="music-content">
              <h3>آهنگ برای {{ s.childName }}</h3>
              <span class="date">{{ formatDate(s.createdAt) }}</span>
              <div class="lyrics-snippet">{{ truncate(s.lyrics, 60) }}</div>
            </div>
             <button class="btn-details" @click="openModal(s, 'music')">جزئیات و تحلیل</button>
           </div>
        </div>
      </div>

      <!-- 7. CONNECTION TAB -->
      <div v-else-if="tab==='connection' && !isLoadingAny" class="tab-content">
         <div v-if="connectionSessions.length === 0" class="empty"><div class="empty-icon">🔗</div><p>هنوز ماموریتی انجام نشده است.</p></div>
         <div v-else class="grid-layout">
            <div v-for="s in connectionSessions" :key="s.id" class="card-item connection-card">
               <div class="conn-image">
                  <img v-if="getLastStepImage(s)" :src="getLastStepImage(s)" loading="lazy" />
                  <div class="mission-badge" :class="s.missionType">
                     {{ getMissionLabel(s.missionType) }}
                  </div>
               </div>
               <div class="card-meta">
                  <div class="meta-row">
                    <span>وضعیت:</span>
                    <span class="status-pill" :class="s.status">{{ s.status === 'completed' ? 'تکمیل شده ✅' : 'در حال انجام ⏳' }}</span>
                  </div>
                  <div class="meta-row"><span>تاریخ:</span> <strong>{{ formatDate(s.createdAt) }}</strong></div>
                  <div class="meta-row" v-if="s.analysis?.magicTitle"><span>عنوان:</span> <strong>{{ s.analysis.magicTitle }}</strong></div>
                  
                  <button class="btn-details" @click="openModal(s, 'connection')">
                    {{ s.analysis ? 'مشاهده تحلیل 📊' : 'در حال پردازش...' }}
                  </button>
               </div>
            </div>
         </div>
      </div>

      <!-- 8. SECURITY TAB -->
      <div v-else-if="tab==='security'" class="security-panel">
         <div class="sec-card">
           <div class="sec-icon">🛡️</div>
           <h3>تنظیمات امنیتی اتاق</h3>
           <p class="sec-desc">برای جلوگیری از خروج کودک از محیط بازی، می‌توانید یک قفل ساده تنظیم کنید.</p>
           
           <div class="sec-options">
              <label class="sec-radio" :class="{selected: secType==='none'}">
                <input type="radio" v-model="secType" value="none">
                <span>بدون قفل 🔓</span>
              </label>
              <label class="sec-radio" :class="{selected: secType==='pin'}">
                <input type="radio" v-model="secType" value="pin">
                <span>رمز عددی (PIN) 🔢</span>
              </label>
              <label class="sec-radio" :class="{selected: secType==='pattern'}">
                <input type="radio" v-model="secType" value="pattern">
                <span>الگو (Pattern) 💠</span>
              </label>
           </div>

           <div v-if="secType==='pin'" class="sec-input-wrap">
             <label>رمز 4 رقمی:</label>
             <input v-model="secValue" maxlength="4" placeholder="مثال: 1234" class="pin-input" />
           </div>
           
           <div v-if="secType==='pattern'" class="sec-input-wrap">
             <label>الگو:</label>
             <div class="pattern-wrapper">
               <PatternInput v-model="secValue" />
             </div>
             <small>نقاط را به ترتیب لمس کنید.</small>
           </div>
           
           <button class="btn-save" @click="saveSec" :disabled="secSaving">
             {{ secSaving ? 'در حال ذخیره...' : 'ذخیره تغییرات' }}
           </button>
         </div>
      </div>

      <!-- UNIVERSAL MODAL -->
      <div v-if="showPuzzleModal" class="modal-overlay" @click.self="showPuzzleModal=false">
        <div class="modal-card">
          <button class="close-btn" @click="showPuzzleModal=false">✕</button>
          
          <h3 class="modal-title">
             {{ getModalTitle(modalType) }}
          </h3>
          
          <div class="modal-body" v-if="selectedSession">
             
             <!-- Connection Modal Content -->
             <template v-if="modalType==='connection'">
                <div class="conn-modal-img" v-if="getLastStepImage(selectedSession)">
                   <img :src="getLastStepImage(selectedSession)" />
                </div>
                <div class="analysis-content" v-if="selectedSession.analysis">
                   <div class="magic-title-box">
                      <h4>{{ selectedSession.analysis.magicTitle }}</h4>
                   </div>
                   
                   <div class="callout positive">
                      <div class="callout-title">کپشن بامزه</div>
                      <div class="callout-text">"{{ selectedSession.analysis.funnyCaption }}"</div>
                   </div>

                   <div class="score-row">
                      <div class="score">
                         <div class="score-header">
                            <span class="score-label">امتیاز انرژی</span>
                            <span class="score-value">{{ selectedSession.analysis.powerScore }}/100</span>
                         </div>
                         <div class="score-bar"><div class="score-fill" :style="{ width: selectedSession.analysis.powerScore + '%' }"></div></div>
                      </div>
                   </div>

                   <div class="badges-row" v-if="selectedSession.analysis.badges">
                      <span v-for="b in selectedSession.analysis.badges" :key="b" class="badge">🏅 {{ b }}</span>
                   </div>
                   
                   <div class="callout parent-advice" v-if="selectedSession.analysis.superheroName">
                      <div class="callout-title">تیم ابرقهرمانی</div>
                      <div class="callout-text">{{ selectedSession.analysis.superheroName }}</div>
                   </div>
                </div>
                <div v-else class="pending">
                   <p>هنوز تحلیلی برای این ماموریت ثبت نشده است.</p>
                </div>
             </template>

             <!-- Puzzle/Shapes Modal Content -->
             <template v-else-if="['puzzle', 'shapes', 'letters'].includes(modalType)">
                <div class="image-pair modal-pair">
                  <div class="img-col">
                    <span class="img-label">الگو</span>
                    <img v-if="getImage(selectedSession, 'ref')" :src="getImage(selectedSession, 'ref')" />
                  </div>
                  <div class="img-col">
                    <span class="img-label">کار کودک</span>
                    <img v-if="getImage(selectedSession, 'user')" :src="getImage(selectedSession, 'user')" />
                  </div>
                </div>

                <div v-if="selectedSession.analysisStatus === 'pending'" class="pending">
                  <span class="spinner-small" />
                  <span>تحلیل در حال انجام است...</span>
                </div>
                <div v-else>
                  <div class="callout positive">
                    <div class="callout-title">پیام تشویقی</div>
                    <div class="callout-text">{{ selectedSession.positiveFeedback || '—' }}</div>
                  </div>
                  
                  <div class="details-grid">
                    <template v-if="modalType === 'puzzle'">
                      <div class="detail"><div class="detail-label">حل مسئله</div><div class="detail-text">{{ selectedSession.problemSolvingScore }}/10</div></div>
                      <div class="detail"><div class="detail-label">وضعیت</div><div class="detail-text">{{ selectedSession.isSolved ? 'حل شده' : 'ناتمام' }}</div></div>
                    </template>
                    <template v-if="modalType === 'shapes'">
                       <div class="detail"><div class="detail-label">تطبیق اشکال</div><div class="detail-text">{{ selectedSession.shapeMatchingScore }}/10</div></div>
                    </template>
                    <template v-if="modalType === 'letters'">
                       <div class="detail"><div class="detail-label">شکل‌دهی</div><div class="detail-text">{{ selectedSession.letterFormationScore }}/10</div></div>
                       <div class="detail"><div class="detail-label">صحت</div><div class="detail-text">{{ selectedSession.isCorrect ? 'صحیح' : 'نیاز به تمرین' }}</div></div>
                    </template>
                     <div class="detail"><div class="detail-label">تمرکز</div><div class="detail-text">{{ selectedSession.focusLevel || '—' }}</div></div>
                  </div>

                  <div class="callout parent-advice">
                    <div class="callout-title">راهنمای والدین</div>
                    <div class="callout-text">{{ selectedSession.analysisSummary || '—' }}</div>
                  </div>
                  
                  <div class="tags">
                     <span v-for="tag in (selectedSession.tags||[])" :key="tag" class="tag">#{{ tag }}</span>
                  </div>
                </div>
             </template>

             <!-- Music Modal -->
             <template v-else-if="modalType === 'music'">
                <div class="callout lyrics-box">
                   <div class="callout-title">متن آهنگ (برای {{ selectedSession.childName }})</div>
                   <div class="callout-text lyrics-text">{{ selectedSession.lyrics }}</div>
                </div>
                
                <div v-if="selectedSession.analysisStatus==='pending'" class="pending">
                  <span class="spinner-small" />
                  <span>تحلیل در حال انجام است...</span>
                </div>
                <div v-else>
                   <div class="callout positive">
                     <div class="callout-title">بازخورد</div>
                     <div class="callout-text">{{ selectedSession.positiveFeedback }}</div>
                   </div>
                   
                   <div class="score-row">
                      <div class="score">
                         <div class="score-header"><span class="score-label">خلاقیت</span><span class="score-value">{{ safeScore(selectedSession.creativityScore) }}/10</span></div>
                         <div class="score-bar"><div class="score-fill" :style="{ width: safeScore(selectedSession.creativityScore)*10 + '%' }"></div></div>
                      </div>
                   </div>
                   
                   <div class="details-grid">
                      <div class="detail"><div class="detail-label">لحن</div><div class="detail-text">{{ selectedSession.emotionalTone || '—' }}</div></div>
                      <div class="detail"><div class="detail-label">قافیه</div><div class="detail-text">{{ selectedSession.rhymeQuality || '—' }}</div></div>
                   </div>
                   
                   <div class="callout parent-advice">
                      <div class="callout-title">راهنمای والدین</div>
                      <div class="callout-text">{{ selectedSession.parentAdvice || selectedSession.analysisSummary }}</div>
                   </div>
                </div>
             </template>

             <!-- Story Modal -->
             <template v-else-if="modalType === 'story'">
                <div v-if="selectedSession.analysisStatus==='pending'" class="pending">
                   <p>درخواست تحلیل ارسال شد. لطفاً کمی صبر کنید و صفحه را بروزرسانی کنید.</p>
                </div>
                <div v-else-if="!selectedSession.kidRating" class="pending">
                   <p>برای مشاهده تحلیل، روی دکمه "درخواست تحلیل" کلیک کنید.</p>
                   <button class="btn-action" @click="requestStoryAnalysis(selectedSession)">درخواست تحلیل 🪄</button>
                </div>
                <div v-else>
                   <div class="callout positive">
                      <div class="callout-text">{{ selectedSession.positiveFeedback }}</div>
                   </div>
                   
                   <div class="score-row">
                      <div class="score">
                         <div class="score-header"><span class="score-label">امتیاز تعامل کودک</span><span class="score-value">{{ safeScore(selectedSession.kidRating) }}/10</span></div>
                         <div class="score-bar"><div class="score-fill" :style="{ width: safeScore(selectedSession.kidRating)*10 + '%' }"></div></div>
                      </div>
                   </div>

                   <div class="details-grid">
                      <div class="detail"><div class="detail-label">مهارت ارتباطی</div><div class="detail-text">{{ selectedSession.communicationSkills }}</div></div>
                      <div class="detail"><div class="detail-label">لحن</div><div class="detail-text">{{ selectedSession.emotionalTone }}</div></div>
                   </div>

                   <div class="callout">
                      <div class="callout-title">درک داستان</div>
                      <div class="callout-text">{{ selectedSession.storyUnderstanding || selectedSession.analysisSummary }}</div>
                   </div>
                   <div class="callout parent-advice">
                      <div class="callout-title">راهنمای والدین</div>
                      <div class="callout-text">{{ selectedSession.parentAdvice }}</div>
                   </div>
                   <div class="tags"><span v-for="tag in (selectedSession.tags||[])" :key="tag" class="tag">#{{ tag }}</span></div>
                </div>
             </template>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import PatternInput from '../components/PatternInput.vue'
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { 
   ROOM_GALLERY_QUERY, 
   ROOM_PUZZLE_SESSIONS_QUERY, 
   ROOM_SHAPES_SESSIONS_QUERY, 
   ROOM_STORY_SESSIONS_QUERY, 
   ROOM_LETTERS_SESSIONS_QUERY, 
   ROOM_MUSIC_SESSIONS_QUERY,
   ROOM_CONNECTION_SESSIONS_QUERY 
} from '../api/queries'
import { ANALYZE_STORY_MUTATION } from '../api/mutations'
import { gql } from '@apollo/client/core'

const route = useRoute()
const slug = route.params.slug
const tab = ref('paint')
const currentIndex = ref(0)
const showPuzzleModal = ref(false)
const selectedSession = ref(null)
const modalType = ref('')

// Queries
const { result: rPublic } = useQuery(gql`query RName($slug:String!){roomPublic(slug:$slug){name}}`, { slug })
const roomName = computed(() => rPublic.value?.roomPublic?.name)

const { result, loading: paintLoading } = useQuery(ROOM_GALLERY_QUERY, { slug, gameType: 'paint' }, { fetchPolicy: 'cache-and-network' })
const gallery = computed(() => result.value?.roomGallery || [])
const currentArt = computed(() => gallery.value[currentIndex.value])

const { result: puzzleResult, loading: puzzleLoading } = useQuery(ROOM_PUZZLE_SESSIONS_QUERY, { slug }, { fetchPolicy: 'cache-and-network' })
const puzzles = computed(() => puzzleResult.value?.roomPuzzleSessions || [])

const { result: shapesResult, loading: shapesLoading } = useQuery(ROOM_SHAPES_SESSIONS_QUERY, { slug }, { fetchPolicy: 'cache-and-network' })
const shapes = computed(() => shapesResult.value?.roomShapesSessions || [])

const { result: lettersResult, loading: lettersLoading } = useQuery(ROOM_LETTERS_SESSIONS_QUERY, { slug }, { fetchPolicy: 'cache-and-network' })
const letters = computed(() => lettersResult.value?.roomLettersSessions || [])

const { result: storyResult, loading: storyLoading, refetch: storyRefetch } = useQuery(ROOM_STORY_SESSIONS_QUERY, { slug }, { fetchPolicy: 'cache-and-network' })
const storySessions = computed(() => storyResult.value?.roomStorySessions || [])

const { result: musicResult, loading: musicLoading } = useQuery(ROOM_MUSIC_SESSIONS_QUERY, { slug }, { fetchPolicy: 'cache-and-network' })
const musicSessions = computed(() => musicResult.value?.roomMusicSessions || [])

const { result: connResult, loading: connectionLoading } = useQuery(ROOM_CONNECTION_SESSIONS_QUERY, { slug }, { fetchPolicy: 'cache-and-network' })
const connectionSessions = computed(() => connResult.value?.roomConnectionSessions || [])

const isLoadingAny = computed(() => 
  (tab.value === 'paint' && paintLoading.value) ||
  (tab.value === 'puzzle' && puzzleLoading.value) ||
  (tab.value === 'shapes' && shapesLoading.value) ||
  (tab.value === 'letters' && lettersLoading.value) ||
  (tab.value === 'story' && storyLoading.value) ||
  (tab.value === 'music' && musicLoading.value) ||
  (tab.value === 'connection' && connectionLoading.value)
)

// Security
const secType = ref('none') 
const secValue = ref('')
const secSaving = ref(false)
const R_SEC = gql`query RSec($slug:String!){roomPublic(slug:$slug){slug securityType}}`
const U_SEC = gql`mutation USec($data:UpdateRoomSecurityInput!){updateRoomSecurity(data:$data)}`
const { onResult: onRS } = useQuery(R_SEC, {slug}, {fetchPolicy:'network-only'})
onRS(r=>{ if(r.data?.roomPublic) secType.value=r.data.roomPublic.securityType||'none' })
const { mutate: execUpd } = useMutation(U_SEC)

async function saveSec(){
  secSaving.value=true
  try{ await execUpd({data:{roomSlug:slug,securityType:secType.value,securityValue:secValue.value}}); alert('تنظیمات امنیتی ذخیره شد'); }
  catch(e){ alert('خطا در ذخیره تنظیمات'); }
  finally{ secSaving.value=false }
}

const { mutate: analyzeStory } = useMutation(ANALYZE_STORY_MUTATION)

// Helpers
function safeScore(n) { const v = Number(n||0); return Math.max(0, Math.min(10, v)) }
function formatDate(iso) { if (!iso) return ''; return new Date(iso).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }
function fmtTime(s) { const m = Math.floor((s||0)/60).toString().padStart(2,'0'); const sec = Math.floor((s||0)%60).toString().padStart(2,'0'); return `${m}:${sec}` }
function nextSlide() { if (currentIndex.value < gallery.value.length - 1) currentIndex.value++ }
function prevSlide() { if (currentIndex.value > 0) currentIndex.value-- }
function truncate(str, n) { return (str && str.length > n) ? str.substr(0, n-1) + '...' : str; }

function openModal(s, type) { 
  selectedSession.value = s
  modalType.value = type
  showPuzzleModal.value = true 
}

function getModalTitle(type) {
  const titles = {
    'puzzle': 'تحلیل پازل 🧩',
    'shapes': 'تحلیل اشکال 🔺',
    'letters': 'تحلیل حروف 🔤',
    'music': 'تحلیل موسیقی 🎵',
    'story': 'تحلیل داستان 📖',
    'connection': 'تحلیل ماموریت 🔗'
  }
  return titles[type] || 'تحلیل هوش مصنوعی'
}

function getMissionLabel(type) {
  const map = { kitchen: 'آشپزخونه 🍳', workshop: 'کارگاه 🛠️', league: 'لیگ قهرمانان 🦸' }
  return map[type] || type
}

function getLastStepImage(session) {
  if (!session.steps || session.steps.length === 0) return null
  return session.steps[session.steps.length - 1].imageBase64
}

function getImage(session, type) {
  if (modalType.value === 'puzzle') return type === 'ref' ? session.imageBase64 : session.solutionBase64
  if (modalType.value === 'shapes') return type === 'ref' ? session.referenceBase64 : session.compositionBase64
  if (modalType.value === 'letters') return type === 'ref' ? session.referenceBase64 : session.traceBase64
  return null
}

async function requestStoryAnalysis(sess) {
  try {
    await analyzeStory({ data: { sessionId: sess.id } })
    sess.analysisStatus = 'pending' // Optimistic update
    setTimeout(() => storyRefetch(), 2000)
  } catch (e) { alert('خطا در درخواست تحلیل') }
}
</script>

<style scoped>
/* Base */
.container { padding: 30px; max-width: 1000px; margin: 0 auto; min-height: 100vh; font-family: 'Vazirmatn', sans-serif; color: #334155; }
.header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
.header h1 { font-size: 28px; font-weight: 900; margin: 0; color: #1e293b; letter-spacing: -0.5px; }
.room-subtitle { font-size: 14px; color: #64748b; font-weight: 500; }
.back-btn { text-decoration: none; color: #475569; border: 1px solid #cbd5e1; padding: 8px 16px; border-radius: 12px; transition: all 0.2s; font-weight: 600; background: #fff; font-size: 14px; }
.back-btn:hover { background: #f1f5f9; border-color: #94a3b8; }

/* Tabs */
.gallery-section { background: white; padding: 24px; border-radius: 24px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05); }
.tabs { display:flex; gap:8px; margin-bottom:24px; overflow-x: auto; padding-bottom: 4px; border-bottom: 1px solid #f1f5f9; }
.tabs button { background: transparent; color:#64748b; border:none; border-radius:10px; padding:10px 14px; cursor:pointer; font-weight:600; white-space: nowrap; transition: 0.2s; font-size: 14px; }
.tabs button:hover { background: #f8fafc; color: #334155; }
.tabs button.active { background:#eff6ff; color: #2563eb; font-weight: 700; }

h2 { margin-bottom: 20px; color: #1e293b; font-size: 18px; font-weight: 800; border-right: 4px solid #3b82f6; padding-right: 12px; }

/* Loading/Empty */
.loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px; color: #94a3b8; }
.spinner { width: 32px; height: 32px; border: 3px solid #e2e8f0; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s infinite linear; margin-bottom: 16px; }
.empty { text-align: center; color: #94a3b8; padding: 60px; background: #f8fafc; border-radius: 20px; border: 2px dashed #e2e8f0; }
.empty-icon { font-size: 48px; margin-bottom: 16px; opacity: 0.5; }

/* Grids */
.grid-layout { display:grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap:20px; }
.card-item { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:16px; transition: transform 0.2s, box-shadow 0.2s; }
.card-item:hover { transform: translateY(-4px); box-shadow: 0 10px 25px rgba(0,0,0,0.05); }

/* Puzzle/Shapes Images */
.image-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.img-col { display: flex; flex-direction: column; gap: 6px; }
.img-label { font-size: 11px; color: #64748b; font-weight: 600; }
.card-item img { width:100%; height:140px; object-fit:cover; border-radius:10px; background: #f1f5f9; border: 1px solid #f1f5f9; }
.card-meta { display: flex; flex-direction: column; gap: 8px; }
.meta-row { display:flex; justify-content:space-between; color:#475569; font-size: 13px; }
.btn-details { background:#f1f5f9; color:#334155; border:none; border-radius:8px; padding:10px; cursor:pointer; font-weight: 600; width: 100%; margin-top: 8px; transition: 0.2s; }
.btn-details:hover { background: #e2e8f0; }

/* Paint Slider */
.slider-container { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 24px; position: relative; }
.slider-wrapper { flex: 1; }
.nav-btn { background: #fff; border: 1px solid #e2e8f0; width: 40px; height: 40px; border-radius: 50%; cursor: pointer; color: #64748b; font-size: 18px; display: flex; align-items: center; justify-content: center; transition: 0.2s; position: absolute; top: 200px; z-index: 10; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
.nav-btn:hover:not(:disabled) { background: #f8fafc; color: #3b82f6; border-color: #bfdbfe; }
.nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.nav-btn.prev { right: -20px; }
.nav-btn.next { left: -20px; }

.image-container { background: #f1f5f9; border-radius: 16px; padding: 12px; margin-bottom: 16px; text-align: center; }
.art-image { max-width: 100%; max-height: 450px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.art-info { background: #fff; }
.info-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid #f1f5f9; padding-bottom: 12px; }
.info-header h3 { margin: 0; font-size: 18px; color: #1e293b; }
.date { color: #94a3b8; font-size: 13px; }

.thumbnails { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 8px; }
.thumb { width: 60px; height: 60px; border-radius: 10px; overflow: hidden; cursor: pointer; opacity: 0.5; border: 2px solid transparent; flex-shrink: 0; transition: 0.2s; }
.thumb.active { opacity: 1; border-color: #3b82f6; transform: scale(1.05); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }

/* Analysis Styles */
.analysis h4 { color: #475569; margin-bottom: 12px; font-size: 14px; font-weight: 700; }
.pending { display: flex; align-items: center; gap: 8px; color: #64748b; font-size: 14px; background: #f8fafc; padding: 12px; border-radius: 8px; }
.spinner-small { width: 16px; height: 16px; border: 2px solid #cbd5e1; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s infinite linear; }
@keyframes spin { to { transform: rotate(360deg); } }

.callout { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin-bottom: 16px; }
.callout.positive { background: #f0fdf4; border-color: #bbf7d0; color: #15803d; }
.callout.parent-advice { background: #eff6ff; border-color: #bfdbfe; color: #1e40af; }
.callout-title { font-size: 12px; font-weight: 700; margin-bottom: 6px; opacity: 0.8; }
.callout-text { font-size: 14px; line-height: 1.6; }

.score-row { margin-bottom: 16px; }
.score-header { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 13px; font-weight: 600; color: #475569; }
.score-value { color: #0f172a; font-weight: 800; }
.score-bar { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.score-fill { height: 100%; background: linear-gradient(90deg, #3b82f6, #6366f1); border-radius: 4px; }

.details-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 12px; margin-bottom: 16px; }
.detail { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; }
.detail-label { font-size: 11px; color: #94a3b8; margin-bottom: 4px; }
.detail-text { font-size: 13px; font-weight: 600; color: #334155; }

.tags { display: flex; gap: 6px; flex-wrap: wrap; }
.tag { background: #f1f5f9; color: #64748b; padding: 4px 8px; border-radius: 6px; font-size: 12px; font-weight: 500; }

/* Story Specific */
.story-grid { display:grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap:20px; }
.story-card { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:16px; display:flex; flex-direction:column; }
.story-head { display:flex; justify-content:space-between; align-items:center; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #f8fafc; }
.story-title { font-weight: 700; color: #334155; }
.story-date { font-size: 12px; color: #94a3b8; }
.chat-preview { background: #f8fafc; border-radius: 12px; padding: 12px; margin-bottom: 12px; display: flex; flex-direction: column; gap: 8px; }
.mini-msg { display: flex; gap: 8px; font-size: 13px; color: #475569; }
.role-icon { font-size: 14px; }
.analysis-preview { margin-bottom: 12px; }
.rating-badge { background: #fffbeb; color: #b45309; padding: 6px 12px; border-radius: 8px; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; border: 1px solid #fcd34d; }
.btn-action { background: #8b5cf6; color: white; border: none; padding: 8px 12px; border-radius: 8px; font-size: 13px; cursor: pointer; }

/* Music Specific */
.music-card { display: flex; flex-direction: column; gap: 12px; text-align: center; }
.music-icon-large { font-size: 40px; margin-bottom: 4px; }
.lyrics-snippet { font-size: 13px; color: #64748b; font-style: italic; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 8px; }
.lyrics-box { max-height: 200px; overflow-y: auto; white-space: pre-wrap; text-align: center; background: #fff1f2; border-color: #fecdd3; color: #9f1239; }
.lyrics-text { font-family: inherit; font-size: 15px; line-height: 2; }

/* Connection Specific */
.conn-image { position: relative; height: 160px; border-radius: 12px; overflow: hidden; margin-bottom: 12px; background: #f1f5f9; border: 1px solid #e2e8f0; }
.conn-image img { width: 100%; height: 100%; object-fit: cover; }
.mission-badge { position: absolute; top: 8px; right: 8px; background: rgba(255,255,255,0.95); padding: 4px 8px; border-radius: 6px; font-size: 11px; font-weight: bold; color: #333; box-shadow: 0 2px 4px rgba(0,0,0,0.1); backdrop-filter: blur(2px); }
.mission-badge.kitchen { color: #ea580c; border-right: 3px solid #ea580c; }
.mission-badge.workshop { color: #2563eb; border-right: 3px solid #2563eb; }
.mission-badge.league { color: #9333ea; border-right: 3px solid #9333ea; }
.status-pill { font-size: 11px; padding: 2px 8px; border-radius: 99px; background: #f1f5f9; color: #64748b; }
.status-pill.completed { background: #dcfce7; color: #166534; }
.conn-modal-img { width: 100%; height: 300px; border-radius: 16px; overflow: hidden; margin-bottom: 24px; background: #f8fafc; border: 1px solid #e2e8f0; }
.conn-modal-img img { width: 100%; height: 100%; object-fit: contain; }
.magic-title-box { text-align: center; margin-bottom: 24px; }
.magic-title-box h4 { font-size: 24px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin: 0; font-weight: 900; }
.badges-row { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 20px; }
.badge { background: #fef9c3; color: #b45309; padding: 6px 12px; border-radius: 99px; font-size: 13px; font-weight: bold; border: 1px solid #fde047; }

/* Security Tab */
.security-panel { max-width: 600px; margin: 0 auto; }
.sec-card { background: #fff; padding: 32px; border-radius: 24px; text-align: center; border: 1px solid #e2e8f0; }
.sec-icon { font-size: 48px; margin-bottom: 16px; }
.sec-desc { color: #64748b; margin-bottom: 24px; font-size: 14px; }
.sec-options { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
.sec-radio { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border: 2px solid #e2e8f0; border-radius: 12px; cursor: pointer; transition: 0.2s; }
.sec-radio.selected { border-color: #3b82f6; background: #eff6ff; color: #1e40af; }
.sec-radio input { accent-color: #3b82f6; }
.sec-input-wrap { margin-bottom: 24px; text-align: right; }
.sec-input-wrap label { display: block; margin-bottom: 8px; font-weight: 600; color: #334155; }
.pin-input { width: 100%; padding: 14px; border: 2px solid #cbd5e1; border-radius: 12px; font-size: 20px; text-align: center; letter-spacing: 4px; outline: none; transition: 0.2s; }
.pin-input:focus { border-color: #3b82f6; }
.pattern-wrapper { display: flex; justify-content: center; background: #f8fafc; padding: 20px; border-radius: 16px; border: 1px solid #e2e8f0; }
.btn-save { background: #2563eb; color: #fff; border: none; padding: 12px 32px; border-radius: 12px; font-weight: 700; font-size: 16px; cursor: pointer; transition: 0.2s; width: 100%; }
.btn-save:hover:not(:disabled) { background: #1d4ed8; }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

/* Modal Overlay */
.modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(4px); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; }
.modal-card { background: #fff; width: 100%; max-width: 550px; border-radius: 24px; max-height: 85vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
.modal-card h3.modal-title { margin: 0; padding: 20px 24px; border-bottom: 1px solid #f1f5f9; font-size: 18px; color: #1e293b; text-align: right; background: #fff; }
.modal-body { padding: 24px; overflow-y: auto; }
.close-btn { position: absolute; top: 16px; left: 16px; width: 32px; height: 32px; border-radius: 50%; background: #f1f5f9; border: none; color: #64748b; font-weight: bold; cursor: pointer; transition: 0.2s; z-index: 10; }
.close-btn:hover { background: #e2e8f0; color: #ef4444; }

.modal-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
.modal-pair img { width: 100%; border-radius: 12px; border: 1px solid #e2e8f0; aspect-ratio: 1; object-fit: contain; background: #f8fafc; }

@keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

/* Mobile */
@media (max-width: 768px) {
  .header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .back-btn { width: 100%; text-align: center; }
  .grid-layout { grid-template-columns: 1fr; }
  .modal-card { height: 100%; max-height: 100%; border-radius: 0; }
  .slider-container { flex-direction: column; position: relative; }
  .nav-btn { top: 50%; transform: translateY(-50%); }
  .nav-btn.prev { right: 0; }
  .nav-btn.next { left: 0; }
}
</style>